'use client'

import { useId, useState, type ReactNode } from 'react'
import { ChevronDown, Loader2, MessageSquare, Plus, Search, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { UserText } from '@/components/ui/user-text'
import { portalTimeAgo } from '@/components/user-portal/learner-i18n'
import { useTranslation } from '@/lib/i18n'
import { fetchKnowledgeConversation, useDeleteKnowledgeConversation, useKnowledgeConversations } from '@/lib/knowledge/portal-api'
import type { ConversationDetail, KnowledgePortalSurface } from '@/lib/knowledge/portal-types'
import { cn } from '@/lib/utils'

/**
 * The drawer header's "New conversation" dropdown, as the main application's
 * drawer has it (`chat-companion.tsx`, drawer mode): the current
 * conversation's title with a chevron; open, it lists this person's
 * conversations on the surface shown (GET /conversations, the latest 50) with a
 * search over their titles and "New conversation" at the foot. A row opens that
 * conversation to read and continue; the bin deletes it after a confirmation
 * (the API soft-deletes it and empties its content). It replaced the separate
 * History view the drawer had before.
 */
export function ConversationMenu({
  surface,
  title,
  activeId,
  disabled,
  onLoad,
  onNew,
  onDeleted,
}: {
  surface: KnowledgePortalSurface
  /** The current conversation's title, or null for a new one. */
  title: string | null
  activeId: string | null
  disabled: boolean
  onLoad: (detail: ConversationDetail) => void
  onNew: () => void
  onDeleted: (id: string) => void
}) {
  const { t, locale } = useTranslation()
  const titleId = useId()
  // Capitals and letter spacing are a Latin-script treatment.
  const latin = locale === 'en' || locale === 'fr'
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [confirming, setConfirming] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const { conversations, isLoading, isError } = useKnowledgeConversations(surface, open)
  const remove = useDeleteKnowledgeConversation(surface)

  const shown = title ?? t('knowledgePortal.panel.newConversation')
  const needle = query.trim().toLocaleLowerCase()
  const rows = conversations
    .map((c) => ({ ...c, label: c.title || t('knowledgePortal.history.untitled') }))
    .filter((c) => !needle || c.label.toLocaleLowerCase().includes(needle))

  const setMenu = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setQuery('')
      setConfirming(null)
      setLoadFailed(false)
    }
  }

  const openConversation = async (id: string) => {
    setLoadFailed(false)
    try {
      onLoad(await fetchKnowledgeConversation(id))
      setMenu(false)
    } catch {
      setLoadFailed(true)
    }
  }

  const confirmDelete = (id: string) => {
    remove.mutate(id, {
      onSuccess: () => {
        setConfirming(null)
        onDeleted(id)
      },
    })
  }

  return (
    <Popover open={open} onOpenChange={setMenu}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          aria-label={t('knowledgePortal.conversations.trigger', { title: shown })}
          className="group/menu h-8 min-w-0 shrink gap-1.5 px-2 font-medium data-[state=open]:bg-accent"
        >
          <MessageSquare className="size-4 shrink-0 text-primary" aria-hidden="true" />
          {title ? (
            <span className="max-w-[12rem] truncate sm:max-w-[16rem]">
              <UserText>{title}</UserText>
            </span>
          ) : (
            <span className="truncate">{shown}</span>
          )}
          <ChevronDown
            className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/menu:rotate-180"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        aria-labelledby={titleId}
        className="flex max-h-[min(26rem,70vh)] w-[min(22rem,calc(100vw-2rem))] flex-col gap-0 p-0"
      >
        <h3 id={titleId} className={cn('px-3 pt-3 pb-1 text-[10px] font-semibold text-muted-foreground', latin && 'tracking-wider uppercase')}>
          {t('knowledgePortal.history.title')}
        </h3>
        <div className="flex items-center gap-2 border-b border-border/60 px-3 pb-2">
          <Search className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label={t('knowledgePortal.conversations.search')}
            placeholder={t('knowledgePortal.conversations.searchPlaceholder')}
            className="h-8 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 dark:bg-transparent"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-1">
          {isLoading ? (
            <p role="status" className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              {t('knowledgePortal.history.loading')}
            </p>
          ) : isError ? (
            <p role="alert" className="px-3 py-4 text-sm text-muted-foreground">
              {t('knowledgePortal.history.error')}
            </p>
          ) : conversations.length === 0 ? (
            <EmptyLine>{t('knowledgePortal.history.empty')}</EmptyLine>
          ) : rows.length === 0 ? (
            <EmptyLine>{t('knowledgePortal.conversations.noMatch')}</EmptyLine>
          ) : (
            <ul>
              {rows.map((c) => (
                <li key={c.id} className="flex items-center gap-1 px-1">
                  <Button
                    type="button"
                    variant="ghost"
                    aria-current={c.id === activeId ? 'true' : undefined}
                    onClick={() => void openConversation(c.id)}
                    className={cn(
                      'h-auto min-w-0 flex-1 justify-between gap-2 px-2 py-1.5 text-start text-[13px] font-normal',
                      c.id === activeId && 'bg-accent font-medium',
                    )}
                  >
                    {/* The typed title keeps its own direction, but sits at the row's start like the others. */}
                    <span className="min-w-0 flex-1 truncate text-start">
                      <UserText>{c.label}</UserText>
                    </span>
                    {c.lastMessageAt ? (
                      <span className="shrink-0 text-[10px] text-muted-foreground">{portalTimeAgo(t, c.lastMessageAt)}</span>
                    ) : null}
                  </Button>
                  {confirming === c.id ? (
                    <div role="group" aria-label={t('knowledgePortal.history.confirm')} className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => confirmDelete(c.id)}
                        className="h-7 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t('knowledgePortal.history.confirmDelete')}
                      </Button>
                      <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setConfirming(null)}>
                        {t('knowledgePortal.history.cancel')}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('knowledgePortal.history.delete', { title: c.label })}
                      onClick={() => setConfirming(c.id)}
                      className="size-7 shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="size-3.5" aria-hidden="true" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {loadFailed ? (
            <p role="alert" className="px-3 py-2 text-xs text-destructive">
              {t('knowledgePortal.history.openError')}
            </p>
          ) : null}
        </div>

        <div className="border-t p-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onNew()
              setMenu(false)
            }}
            className="w-full justify-start gap-2 text-primary hover:text-primary"
          >
            <Plus className="size-3.5" aria-hidden="true" />
            {t('knowledgePortal.panel.newConversation')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function EmptyLine({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-3 py-6 text-muted-foreground">
      <MessageSquare className="size-5 opacity-40" aria-hidden="true" />
      <p className="text-xs">{children}</p>
    </div>
  )
}
